/**
 * Firestore write helpers for the guest list.
 *
 * Reads are owned by `useGuests` / `useGroups` hooks; everything mutating
 * lives here so the screen and modal stay declarative. Each function takes
 * the weddingId explicitly — easier to test, no implicit context.
 */

import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { track } from "@/lib/analytics";
import i18n from "@/lib/i18n";
import type {
  AccommodationPayment,
  Guest,
  InvitationChannel,
  Table,
  WeddingCoupleRole,
} from "@amazing-wedding/shared-types";
import { guestSeatingFieldPath } from "@amazing-wedding/shared-types";
import {
  guestRef,
  planTableRef,
  planTablesCol,
  weddingRef,
} from "@/features/seating/firestore-paths";
import {
  enforceAnonymousPlusOneSeating,
  enforceLinkedCompanionSeating,
  enforceUnlinkedCompanionSeating,
  plusOneBecameAnonymous,
} from "./companion-seating-guard";
import { unassignGuestWithSeatMap } from "@/features/seating/operations";
import { projectCreatedGuest, syncAttendeeCount } from "./sync-attendee-count";
import {
  collectCompanionGuestIds,
  findLegacyLinkTargets,
  findStalePartnerPairIds,
  getCanonicalCoupleIds,
  hasHouseholdMembers,
  isCouplePlaceholderLastName,
  isGuestShownAsCompanion,
  isWeddingCoupleGuest,
  personNameKey,
  pickCanonicalCouplePair,
  resolveWeddingRole,
} from "./guest-unit-rows";

/** Fields a caller supplies when creating a new guest. */
export type GuestDraft = Omit<
  Guest,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "deletedAt"
  | "rsvpRespondedAt"
  | "guestCount"
  | "rsvpToken"
  | "rsvpSubmittedToken"
>;

/** Fields a caller may edit. Cannot edit timestamps or id directly. */
export type GuestPatch = Partial<Omit<Guest, "id" | "createdAt" | "updatedAt" | "deletedAt">>;

/**
 * Fields blanked on the companion's own doc when they become linked as
 * another guest's plus-one or child. The host's `plusOne.*` (or `children[].*`)
 * is the only source of truth while linked — anything stranded here would
 * resurface stale if the link is later cleared.
 */
const COMPANION_CLEAR_PATCH: GuestPatch = {
  phone: null,
  rsvpStatus: "pending",
  mealPreference: null,
  accommodationRequired: false,
  notes: null,
};

export async function createGuest(weddingId: string, draft: GuestDraft) {
  const col = collection(db, "weddings", weddingId, "guests");
  const prior = await getDocs(query(col, limit(1)));
  const isFirstGuest = prior.empty;
  const ref = await addDoc(col, {
    ...draft,
    deletedAt: null,
    rsvpRespondedAt: null,
    rsvpToken: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  if (isFirstGuest) {
    track("first_guest_added", { wedding_id: weddingId });
  }
  return ref.id;
}

function projectAfterLegacyLink(
  allGuests: Guest[],
  newGuest: Pick<Guest, "id" | "firstName" | "lastName">,
  targets: ReturnType<typeof findLegacyLinkTargets>,
): Guest[] {
  const inserted = projectCreatedGuest(newGuest.id, {
    firstName: newGuest.firstName,
    lastName: newGuest.lastName,
    plusOne: null,
    children: [],
    plusOneAllowed: false,
    deletedAt: null,
  });
  const withNew = allGuests.some((g) => g.id === newGuest.id)
    ? allGuests
    : [...allGuests, { ...inserted, ...COMPANION_CLEAR_PATCH }];

  return withNew.map((g) => {
    if (g.id === newGuest.id) {
      return { ...g, ...COMPANION_CLEAR_PATCH };
    }
    for (const target of targets) {
      if (g.id !== target.hostId) continue;
      if (target.kind === "plusOne" && g.plusOne) {
        return {
          ...g,
          plusOne: { ...g.plusOne, guestId: newGuest.id },
        };
      }
      if (target.kind === "child") {
        return {
          ...g,
          children: (g.children ?? []).map((c) =>
            c.id === target.childId ? { ...c, guestId: newGuest.id } : c,
          ),
        };
      }
    }
    return g;
  });
}

/**
 * After creating a guest, link any legacy plus-one/child name matches on other
 * hosts so the new doc appears under the host instead of as a hidden duplicate.
 */
export async function linkLegacyCompanionReferences(
  weddingId: string,
  newGuest: Pick<Guest, "id" | "firstName" | "lastName">,
  allGuests: Guest[],
): Promise<Guest[] | null> {
  const targets = findLegacyLinkTargets(newGuest, allGuests);
  if (targets.length === 0) return null;

  const batch = writeBatch(db);
  const byId = new Map(allGuests.map((g) => [g.id, g]));

  for (const target of targets) {
    const host = byId.get(target.hostId);
    if (!host) continue;
    const ref = doc(db, "weddings", weddingId, "guests", target.hostId);

    if (target.kind === "plusOne" && host.plusOne) {
      batch.update(ref, {
        plusOne: { ...host.plusOne, guestId: newGuest.id },
        updatedAt: serverTimestamp(),
      });
    } else if (target.kind === "child") {
      const children = (host.children ?? []).map((c) =>
        c.id === target.childId ? { ...c, guestId: newGuest.id } : c,
      );
      batch.update(ref, { children, updatedAt: serverTimestamp() });
    }
  }

  // The new guest is now a linked companion; clear per-person fields so
  // host.plusOne.* (or host.children[].*) stays the single source of truth.
  batch.update(doc(db, "weddings", weddingId, "guests", newGuest.id), {
    ...COMPANION_CLEAR_PATCH,
    updatedAt: serverTimestamp(),
  });

  await batch.commit();

  for (const target of targets) {
    if (target.kind === "plusOne") {
      await enforceLinkedCompanionSeating(weddingId, target.hostId, newGuest.id);
    }
  }

  return projectAfterLegacyLink(allGuests, newGuest, targets);
}

/** Clear plus-one/child guestId pointers when a linked guest is soft-deleted. */
export async function clearCompanionReferencesOnDelete(
  weddingId: string,
  deletedGuestId: string,
  allGuests: Guest[],
) {
  const batch = writeBatch(db);
  let pending = 0;

  for (const host of allGuests) {
    if (host.id === deletedGuestId) continue;
    let patch: Record<string, unknown> | null = null;

    if (host.plusOne?.guestId?.trim() === deletedGuestId) {
      patch = { plusOne: { ...host.plusOne, guestId: null } };
    }

    const childIdx = (host.children ?? []).findIndex((c) => c.guestId?.trim() === deletedGuestId);
    if (childIdx >= 0) {
      const children = [...(host.children ?? [])];
      const child = children[childIdx];
      if (child) children[childIdx] = { ...child, guestId: null };
      patch = { ...(patch ?? {}), children };
    }

    if (patch) {
      batch.update(doc(db, "weddings", weddingId, "guests", host.id), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
      pending++;
    }
  }

  if (pending > 0) await batch.commit();
}

/** Fields that cannot be changed on system wedding-couple guests. */
const SYSTEM_COUPLE_PROTECTED = new Set<keyof GuestPatch>([
  "firstName",
  "lastName",
  "groupId",
  "plusOne",
  "plusOneAllowed",
  "rsvpToken",
  "rsvpSubmittedToken",
  "weddingRole",
  "partnerId",
  "rsvpStatus",
  "accommodationRequired",
]);

export function stripSystemCouplePatch(patch: GuestPatch): GuestPatch {
  const next = { ...patch };
  for (const key of SYSTEM_COUPLE_PROTECTED) {
    delete next[key];
  }
  return next;
}

/** Stamp first response time when status moves pending → accepted/declined. */
export function withRsvpRespondedAtOnFirstResponse(guest: Guest, patch: GuestPatch): GuestPatch {
  if (!patch.rsvpStatus || patch.rsvpStatus === "pending") return patch;
  if (guest.rsvpStatus !== "pending") return patch;
  if (guest.rsvpRespondedAt || patch.rsvpRespondedAt) return patch;
  return { ...patch, rsvpRespondedAt: new Date().toISOString() };
}

export async function updateGuest(
  weddingId: string,
  guestId: string,
  patch: GuestPatch,
  allGuests?: Guest[],
) {
  const ref = doc(db, "weddings", weddingId, "guests", guestId);
  const snap = await getDoc(ref);
  let finalPatch = patch;
  if (snap.exists()) {
    const guest = { id: snap.id, ...snap.data() } as Guest;
    if (isWeddingCoupleGuest(guest)) {
      finalPatch = stripSystemCouplePatch(patch);
    }
    finalPatch = withRsvpRespondedAtOnFirstResponse(guest, finalPatch);
  }
  const shouldEnforceAnonymousPlusOne =
    snap.exists() && plusOneBecameAnonymous({ id: snap.id, ...snap.data() } as Guest, finalPatch);

  await updateDoc(ref, {
    ...finalPatch,
    updatedAt: serverTimestamp(),
  });

  if (shouldEnforceAnonymousPlusOne) {
    await enforceAnonymousPlusOneSeating(weddingId, guestId);
  }

  if (allGuests) {
    await syncAttendeeCount(
      weddingId,
      allGuests.map((g) => (g.id === guestId ? { ...g, ...finalPatch } : g)),
    );
  }
}

/**
 * Record (or undo) a hand-off of the guest's RSVP link via the Send sheet.
 * Pass a channel + ISO timestamp on send; pass `null` to clear (the "Cofnij"
 * toast action). Delivery axis only — does not touch `rsvpStatus`.
 */
export async function markInvitationSent(
  weddingId: string,
  guestId: string,
  channel: InvitationChannel | null,
) {
  await updateGuest(weddingId, guestId, {
    invitationChannel: channel,
    invitationSentAt: channel ? new Date().toISOString() : null,
  });
}

/**
 * Write the per-guest accommodation payment record. Pass `null` to clear
 * (the rollup will then fall back to the "full" default). The mode is the
 * single source of truth for how the couple is funding this guest's stay —
 * see `AccommodationPaymentModeSchema` for the contract.
 */
export async function setGuestAccommodationPayment(
  weddingId: string,
  guestId: string,
  payment: AccommodationPayment | null,
) {
  const ref = doc(db, "weddings", weddingId, "guests", guestId);
  if (payment === null) {
    await updateDoc(ref, {
      accommodationPayment: deleteField(),
      updatedAt: serverTimestamp(),
    });
    return;
  }
  // Persist a per-mode field set so the document validates against the
  // discriminated-union schema: `full` carries no `coupleContribution`,
  // `partial` carries the amount (null when the user has not entered one
  // yet). Writing a `null` field on `full` would round-trip back through
  // Zod as a parse error.
  const value =
    payment.mode === "partial"
      ? { mode: "partial" as const, coupleContribution: payment.coupleContribution ?? null }
      : { mode: "full" as const };
  await updateDoc(ref, {
    accommodationPayment: value,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Soft-delete only — sets `deletedAt` to a server timestamp. The list hook
 * filters these out by default. Hard deletion (after 30 days) is a future
 * Cloud Function; v0.2 doesn't run it.
 */
export async function softDeleteGuest(weddingId: string, guestId: string, allGuests: Guest[] = []) {
  const existing = allGuests.find((g) => g.id === guestId);
  if (existing && isWeddingCoupleGuest(existing)) {
    throw new Error("Pan Młody i Pani Młoda nie mogą zostać usunięci.");
  }

  const gRef = guestRef(weddingId, guestId);
  const [guestSnap, weddingSnap] = await Promise.all([getDoc(gRef), getDoc(weddingRef(weddingId))]);

  if (guestSnap.exists()) {
    const snapGuest = { id: guestSnap.id, ...guestSnap.data() } as Guest;
    if (isWeddingCoupleGuest(snapGuest)) {
      throw new Error("Pan Młody i Pani Młoda nie mogą zostać usunięci.");
    }
  }

  if (allGuests.length > 0) {
    await clearCompanionReferencesOnDelete(weddingId, guestId, allGuests);
  }

  if (guestSnap.exists()) {
    const guest = { id: guestSnap.id, ...guestSnap.data() } as Guest;
    const legacyPlanId = weddingSnap.data()?.legacySeatingPlanId as string | undefined;
    const planIds = new Set(Object.keys(guest.seatingByPlan ?? {}));
    if (guest.tableId && legacyPlanId) planIds.add(legacyPlanId);

    for (const planId of planIds) {
      const seating =
        guest.seatingByPlan?.[planId] ??
        (planId === legacyPlanId && guest.tableId
          ? { tableId: guest.tableId, seatNumber: guest.seatNumber ?? null }
          : null);
      if (!seating?.tableId) continue;

      const tableSnap = await getDoc(planTableRef(weddingId, planId, seating.tableId));
      if (tableSnap.exists()) {
        const table = { id: tableSnap.id, ...tableSnap.data() } as Table;
        const planTablesSnap = await getDocs(planTablesCol(weddingId, planId));
        const allTableIds = planTablesSnap.docs.map((d) => d.id);
        await unassignGuestWithSeatMap(
          weddingId,
          planId,
          guestId,
          seating.tableId,
          table,
          { [guestId]: guest },
          allTableIds,
        );
      } else {
        await updateDoc(gRef, {
          [guestSeatingFieldPath(planId)]: deleteField(),
          updatedAt: serverTimestamp(),
        });
      }
    }
  }

  // Final write is a batch so the partner's partnerId is cleared atomically
  // alongside the deletedAt mark — no transient state where the partner
  // points at a soft-deleted ghost.
  const finalBatch = writeBatch(db);
  finalBatch.update(gRef, {
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const guestPartnerId = guestSnap.exists()
    ? ((guestSnap.data() as Guest).partnerId ?? null)
    : (existing?.partnerId ?? null);
  if (guestPartnerId) {
    finalBatch.update(doc(db, "weddings", weddingId, "guests", guestPartnerId), {
      partnerId: null,
      updatedAt: serverTimestamp(),
    });
  }
  await finalBatch.commit();
  if (allGuests.length > 0) {
    const deletedAt = new Date().toISOString();
    await syncAttendeeCount(
      weddingId,
      allGuests.map((g) => (g.id === guestId ? { ...g, deletedAt } : g)),
    );
  }
}

/** Restore a previously soft-deleted guest (no UI for this in v0.2 — utility). */
export async function restoreGuest(weddingId: string, guestId: string, allGuests?: Guest[]) {
  const ref = doc(db, "weddings", weddingId, "guests", guestId);
  await updateDoc(ref, {
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });
  if (allGuests) {
    await syncAttendeeCount(
      weddingId,
      allGuests.map((g) => (g.id === guestId ? { ...g, deletedAt: null } : g)),
    );
  }
}

/**
 * Bulk-clear the groupId on the given guests. Used when a group is deleted
 * so we don't leave orphan references pointing at a non-existent group.
 * Done as a writeBatch so we either clear them all or nothing — single
 * Firestore round-trip.
 */
export async function clearGroupFromGuests(weddingId: string, guestIds: string[]) {
  if (guestIds.length === 0) return;
  const batch = writeBatch(db);
  for (const gid of guestIds) {
    const ref = doc(db, "weddings", weddingId, "guests", gid);
    batch.update(ref, { groupId: null, updatedAt: serverTimestamp() });
  }
  await batch.commit();
}

/**
 * Returns a Polish error message when the companion link is invalid, or null if OK.
 * Drop target = host; dragged guest = companion (osoba towarzysząca).
 */
export function canLinkGuestAsCompanion(
  host: Guest,
  companion: Guest,
  allGuests: Guest[],
): string | null {
  if (host.id === companion.id) {
    return i18n.t("guests.link.self");
  }
  if (isWeddingCoupleGuest(host) || isWeddingCoupleGuest(companion)) {
    return i18n.t("guests.link.weddingCouple");
  }
  if (collectCompanionGuestIds(allGuests).has(companion.id)) {
    return i18n.t("guests.link.alreadyCompanionElsewhere");
  }
  if (isGuestShownAsCompanion(companion, allGuests)) {
    return i18n.t("guests.link.alreadyCompanion");
  }
  const hostPlusOne = host.plusOne;
  if (hostPlusOne?.guestId?.trim()) {
    return i18n.t("guests.link.hostHasCompanion");
  }
  if (hostPlusOne?.firstName?.trim() && !hostPlusOne.guestId?.trim()) {
    // Named legacy plus-one without guestId — slot occupied until cleared in the form.
    return i18n.t("guests.link.hostHasCompanion");
  }
  if (hasHouseholdMembers(companion)) {
    return i18n.t("guests.link.companionHasHousehold");
  }
  return null;
}

/**
 * Links an existing guest as the host's osoba towarzysząca (plusOne with guestId).
 * Anonymous plus-one slots (blank firstName, no guestId) may be replaced.
 */
export async function linkGuestAsCompanion(
  weddingId: string,
  hostGuestId: string,
  companionGuestId: string,
  allGuests: Guest[],
): Promise<string | null> {
  const host = allGuests.find((g) => g.id === hostGuestId);
  const companion = allGuests.find((g) => g.id === companionGuestId);
  if (!host || !companion) {
    return i18n.t("guests.link.notFound");
  }

  const error = canLinkGuestAsCompanion(host, companion, allGuests);
  if (error) return error;

  const batch = writeBatch(db);
  const hostRef = doc(db, "weddings", weddingId, "guests", hostGuestId);
  const companionRef = doc(db, "weddings", weddingId, "guests", companionGuestId);

  batch.update(hostRef, {
    plusOne: {
      firstName: companion.firstName,
      lastName: companion.lastName,
      mealPreference: companion.mealPreference ?? null,
      guestId: companionGuestId,
    },
    plusOneAllowed: true,
    updatedAt: serverTimestamp(),
  });
  // Companion's per-person data is now invisible (host.plusOne shadows it);
  // blank it so unlink doesn't resurface stale state.
  batch.update(companionRef, {
    ...COMPANION_CLEAR_PATCH,
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
  await enforceLinkedCompanionSeating(weddingId, hostGuestId, companionGuestId);
  await syncAttendeeCount(
    weddingId,
    allGuests.map((g) => {
      if (g.id === hostGuestId) {
        return {
          ...g,
          plusOne: {
            firstName: companion.firstName,
            lastName: companion.lastName,
            mealPreference: companion.mealPreference ?? null,
            guestId: companionGuestId,
          },
          plusOneAllowed: true,
        };
      }
      if (g.id === companionGuestId) {
        return { ...g, ...COMPANION_CLEAR_PATCH };
      }
      return g;
    }),
  );
  return null;
}

/**
 * Removes the host's plus-one slot. Anonymous or named-only slots disappear from
 * the list. Linked companions (guestId) are restored as standalone guest rows.
 */
export async function removeHostPlusOne(
  weddingId: string,
  hostGuestId: string,
  allGuests: Guest[],
): Promise<void> {
  const host = allGuests.find((g) => g.id === hostGuestId);
  if (!host?.plusOne) return;

  const plusOne = host.plusOne;
  const linkedId = plusOne.guestId?.trim() || null;
  const batch = writeBatch(db);
  const hostRef = doc(db, "weddings", weddingId, "guests", hostGuestId);

  batch.update(hostRef, {
    plusOne: null,
    updatedAt: serverTimestamp(),
  });

  if (linkedId) {
    batch.update(doc(db, "weddings", weddingId, "guests", linkedId), {
      firstName: plusOne.firstName,
      lastName: plusOne.lastName ?? null,
      mealPreference: plusOne.mealPreference ?? null,
      rsvpStatus: host.rsvpStatus,
      accommodationRequired: host.accommodationRequired,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();

  if (linkedId) {
    await enforceUnlinkedCompanionSeating(weddingId, hostGuestId, linkedId);
  }

  await syncAttendeeCount(
    weddingId,
    allGuests.map((g) => {
      if (g.id === hostGuestId) {
        return { ...g, plusOne: null };
      }
      if (linkedId && g.id === linkedId) {
        return {
          ...g,
          firstName: plusOne.firstName,
          lastName: plusOne.lastName?.trim() || g.lastName,
          mealPreference: plusOne.mealPreference ?? null,
          rsvpStatus: host.rsvpStatus,
          accommodationRequired: host.accommodationRequired,
        };
      }
      return g;
    }),
  );
}

/**
 * Creates the initial wedding couple guest docs and links them as partners.
 * Names come from wedding settings when available; role labels (Pan Młody /
 * Pani Młoda) are UI-only via i18n, not stored as surnames.
 */
function weddingCoupleGuestBase(now: string) {
  return {
    phone: null,
    groupId: null,
    rsvpStatus: "accepted" as const,
    rsvpRespondedAt: null,
    rsvpToken: null,
    rsvpSubmittedToken: null,
    mealPreference: null,
    attendingCeremony: true,
    attendingReception: true,
    attendingAfterParty: false,
    accommodationRequired: true,
    tableId: null,
    seatNumber: null,
    plusOneAllowed: false,
    plusOne: null,
    children: [],
    notes: null,
    importSource: "manual" as const,
    deletedAt: null,
    createdAt: now,
    updatedAt: now,
  };
}

function systemCoupleFirstName(
  role: WeddingCoupleRole,
  wedding?: { coupleName1?: string; coupleName2?: string | null } | null,
): string {
  if (role === "groom") return wedding?.coupleName1?.trim() || "Pan";
  return wedding?.coupleName2?.trim() || "Pani";
}

/**
 * Ensures groom/bride system guests exist, backfills weddingRole, repairs links,
 * deduplicates extra couple docs, and clears stale partnerId pairs.
 */
const ensureCoupleInflight = new Map<string, Promise<void>>();

export async function ensureWeddingCoupleGuests(
  weddingId: string,
  allGuests: Guest[],
  wedding?: { coupleName1?: string; coupleName2?: string | null } | null,
): Promise<void> {
  const existing = ensureCoupleInflight.get(weddingId);
  if (existing) return existing;

  const run = ensureWeddingCoupleGuestsWork(weddingId, allGuests, wedding).finally(() => {
    ensureCoupleInflight.delete(weddingId);
  });
  ensureCoupleInflight.set(weddingId, run);
  return run;
}

async function ensureWeddingCoupleGuestsWork(
  weddingId: string,
  allGuests: Guest[],
  wedding?: { coupleName1?: string; coupleName2?: string | null } | null,
): Promise<void> {
  const batch = writeBatch(db);
  let pending = 0;
  const col = collection(db, "weddings", weddingId, "guests");
  const now = Timestamp.now().toDate().toISOString();

  for (const g of allGuests) {
    if (g.weddingRole) continue;
    const inferred = resolveWeddingRole(g);
    if (!inferred) continue;
    batch.update(doc(col, g.id), {
      weddingRole: inferred,
      updatedAt: serverTimestamp(),
    });
    pending++;
  }

  for (const [aId, bId] of findStalePartnerPairIds(allGuests)) {
    batch.update(doc(col, aId), { partnerId: null, updatedAt: serverTimestamp() });
    batch.update(doc(col, bId), { partnerId: null, updatedAt: serverTimestamp() });
    pending += 2;
  }

  const canonicalIds = getCanonicalCoupleIds(allGuests);
  for (const g of allGuests) {
    if (!isWeddingCoupleGuest(g) || canonicalIds.has(g.id)) continue;

    const hasSeating = Boolean(g.tableId) || Object.keys(g.seatingByPlan ?? {}).length > 0;
    const emptyShell = !hasHouseholdMembers(g) && !hasSeating;

    if (emptyShell) {
      batch.update(doc(col, g.id), {
        deletedAt: serverTimestamp(),
        weddingRole: deleteField(),
        partnerId: null,
        plusOne: null,
        plusOneAllowed: false,
        updatedAt: serverTimestamp(),
      });
    } else {
      batch.update(doc(col, g.id), {
        weddingRole: deleteField(),
        partnerId: null,
        plusOne: null,
        plusOneAllowed: false,
        updatedAt: serverTimestamp(),
      });
    }
    pending++;
  }

  const { groom, bride } = pickCanonicalCouplePair(allGuests);
  const base = weddingCoupleGuestBase(now);

  if (!groom && !bride) {
    const groomRef = doc(col);
    const brideRef = doc(col);
    batch.set(groomRef, {
      ...base,
      firstName: systemCoupleFirstName("groom", wedding),
      lastName: "",
      weddingRole: "groom",
      partnerId: brideRef.id,
    });
    batch.set(brideRef, {
      ...base,
      firstName: systemCoupleFirstName("bride", wedding),
      lastName: "",
      weddingRole: "bride",
      partnerId: groomRef.id,
    });
    pending += 2;
  } else if (groom && !bride) {
    const brideRef = doc(col);
    batch.set(brideRef, {
      ...base,
      firstName: systemCoupleFirstName("bride", wedding),
      lastName: "",
      weddingRole: "bride",
      partnerId: groom.id,
    });
    batch.update(doc(col, groom.id), { partnerId: brideRef.id, updatedAt: serverTimestamp() });
    pending += 2;
  } else if (bride && !groom) {
    const groomRef = doc(col);
    batch.set(groomRef, {
      ...base,
      firstName: systemCoupleFirstName("groom", wedding),
      lastName: "",
      weddingRole: "groom",
      partnerId: bride.id,
    });
    batch.update(doc(col, bride.id), { partnerId: groomRef.id, updatedAt: serverTimestamp() });
    pending += 2;
  } else if (groom && bride) {
    if (groom.partnerId !== bride.id) {
      batch.update(doc(col, groom.id), { partnerId: bride.id, updatedAt: serverTimestamp() });
      pending++;
    }
    if (bride.partnerId !== groom.id) {
      batch.update(doc(col, bride.id), { partnerId: groom.id, updatedAt: serverTimestamp() });
      pending++;
    }
    if (!groom.weddingRole) {
      batch.update(doc(col, groom.id), { weddingRole: "groom", updatedAt: serverTimestamp() });
      pending++;
    }
    if (!bride.weddingRole) {
      batch.update(doc(col, bride.id), { weddingRole: "bride", updatedAt: serverTimestamp() });
      pending++;
    }
    if (!groom.accommodationRequired) {
      batch.update(doc(col, groom.id), {
        accommodationRequired: true,
        updatedAt: serverTimestamp(),
      });
      pending++;
    }
    if (!bride.accommodationRequired) {
      batch.update(doc(col, bride.id), {
        accommodationRequired: true,
        updatedAt: serverTimestamp(),
      });
      pending++;
    }
    for (const coupleGuest of [groom, bride]) {
      if (isCouplePlaceholderLastName(coupleGuest.lastName)) {
        batch.update(doc(col, coupleGuest.id), { lastName: "", updatedAt: serverTimestamp() });
        pending++;
      }
    }
    for (const coupleGuest of [groom, bride]) {
      const partner = coupleGuest.id === groom.id ? bride : groom;
      const plusOne = coupleGuest.plusOne;
      if (!plusOne) continue;
      const linkedId = plusOne.guestId?.trim();
      const nameMatchesPartner =
        plusOne.firstName?.trim() &&
        personNameKey(plusOne.firstName, plusOne.lastName ?? "") ===
          personNameKey(partner.firstName, partner.lastName);
      if (linkedId === partner.id || (!linkedId && nameMatchesPartner)) {
        batch.update(doc(col, coupleGuest.id), {
          plusOne: null,
          plusOneAllowed: false,
          updatedAt: serverTimestamp(),
        });
        pending++;
      }
    }
  }

  if (pending > 0) {
    await batch.commit();
    const snap = await getDocs(col);
    const fresh: Guest[] = snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<Guest, "id">),
    }));
    await syncAttendeeCount(weddingId, fresh);
  }
}

/** @deprecated Use ensureWeddingCoupleGuests — kept for direct calls. */
export async function seedInitialCouple(weddingId: string) {
  await ensureWeddingCoupleGuests(weddingId, [], null);
}
